import { useEffect, useState } from "react";

export interface ElapsedTimerResult {
    elapsed: number;
    formatted: string;
    progressPct: number;
    isOverdue: boolean;
}

/**
 * Бегущий таймер от createdAt до текущего момента (как в legacy useMessageJobStatus).
 *
 * Пока isActive=true — обновляется раз в секунду через setInterval.
 * Когда isActive=false и передан finalDurationSeconds — возвращает финальное
 * значение (длительность завершённой генерации). Иначе — последний tick.
 *
 * Если передан timeoutSec — считает progressPct (0-100) и isOverdue.
 *
 * Формат: HH:MM:SS (legacy совместимость).
 */
export function useElapsedTimer(
    createdAt: string | null | undefined,
    isActive: boolean,
    finalDurationSeconds?: number | null,
    timeoutSec?: number | null,
): ElapsedTimerResult {
    const [elapsed, setElapsed] = useState<number>(() => initialElapsed(createdAt));

    useEffect(() => {
        if (!isActive || !createdAt) return;
        const startMs = new Date(createdAt).getTime();
        const tick = () => setElapsed(Math.max(0, Math.floor((Date.now() - startMs) / 1000)));
        tick();
        const interval = setInterval(tick, 1000);
        return () => clearInterval(interval);
    }, [isActive, createdAt]);

    const seconds =
        !isActive && finalDurationSeconds != null && finalDurationSeconds >= 0
            ? Math.round(finalDurationSeconds)
            : elapsed;

    const progressPct =
        timeoutSec != null && timeoutSec > 0 ? Math.min(100, (seconds / timeoutSec) * 100) : 0;
    const isOverdue = timeoutSec != null && timeoutSec > 0 && seconds > timeoutSec;

    return {
        elapsed: seconds,
        formatted: formatHmsDuration(seconds),
        progressPct,
        isOverdue,
    };
}

function initialElapsed(createdAt: string | null | undefined): number {
    if (!createdAt) return 0;
    return Math.max(0, Math.floor((Date.now() - new Date(createdAt).getTime()) / 1000));
}

export function formatHmsDuration(seconds: number): string {
    const safe = Math.max(0, Math.floor(seconds));
    const h = Math.floor(safe / 3600);
    const m = Math.floor((safe % 3600) / 60);
    const s = safe % 60;
    return `${pad(h)}:${pad(m)}:${pad(s)}`;
}

function pad(n: number): string {
    return String(n).padStart(2, "0");
}
